import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Hourglass, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS = {
  waiting: { label: "Bekliyor", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  offered: { label: "Teklif Edildi", cls: "bg-[#FF5500]/10 text-[#FF5500] border-[#FF5500]/30" },
  booked: { label: "Randevu Alındı", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  expired: { label: "Süresi Doldu", cls: "bg-[#68687B]/10 text-[#68687B] border-[#68687B]/30" },
};

export default function Waitlist() {
  const [items, setItems] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/waitlist").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    setDeleting(true);
    try {
      await api.delete(`/waitlist/${id}`);
      toast.success("Kayıt bekleme listesinden silindi.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDeleting(null); }
  };

  if (items === null) return <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl bg-[#13131C]" />)}</div>;

  return (
    <div data-testid="admin-waitlist-page">
      <PageHeader title="Bekleme Listesi" subtitle="Dolu ders saatleri için sıraya giren üyeler" />
      {items.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-[#68687B]" data-testid="waitlist-empty">
          <Hourglass size={40} className="mb-3 opacity-50" />Bekleme listesi boş.
        </div>
      ) : (
        <div className="rounded-2xl border border-[#222230] overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="waitlist-table">
            <thead className="bg-[#181824] text-[#68687B] text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Üye</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Hizmet</th>
                <th className="px-4 py-3 text-left">Tarih</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Saat</th>
                <th className="px-4 py-3 text-center">Sıra</th>
                <th className="px-4 py-3 text-left">Durum</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((w, i) => {
                const st = STATUS[w.status] || STATUS.waiting;
                return (
                  <tr key={w.id} data-testid={`waitlist-row-${w.id}`} className={`border-t border-[#1A1A24] ${i % 2 ? "bg-[#161622]" : "bg-[#13131C]"}`}>
                    <td className="px-4 py-3 text-white font-medium">{w.member_name}</td>
                    <td className="px-4 py-3 text-[#A0A0B2] hidden sm:table-cell">{w.service_name}</td>
                    <td className="px-4 py-3 text-[#A0A0B2]">{w.date}</td>
                    <td className="px-4 py-3 text-[#A0A0B2] hidden sm:table-cell">{w.start_time}</td>
                    <td className="px-4 py-3 text-center font-mono text-white">{w.position ?? "—"}</td>
                    <td className="px-4 py-3"><span data-testid={`waitlist-status-${w.id}`} className={`text-xs px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span></td>
                    <td className="px-4 py-3 text-right">
                      {w.status === "waiting" && (
                        <Button size="sm" variant="ghost" onClick={() => remove(w.id)} disabled={!!deleting}
                          data-testid={`waitlist-delete-button-${w.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs">
                          {deleting ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
